import { forwardRef } from "react";
import ReactECharts from "echarts-for-react";
import type { TechStatItem } from "@/types/jobMarket";

interface TechBarChartProps {
  data: TechStatItem[];
  compare: boolean;
  currentLabel?: string;
  previousLabel?: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  language: "#0abf53",
  processing: "#10b981",
  streaming: "#06b6d4",
  orchestration: "#8b5cf6",
  transformation: "#f59e0b",
  database: "#3b82f6",
  cloud_dw: "#6366f1",
  cloud: "#f97316",
  infra: "#ef4444",
  data_lake: "#14b8a6",
  bi: "#ec4899",
};

export const TechBarChart = forwardRef<unknown, TechBarChartProps>(function TechBarChart(
  { data, compare, currentLabel = "Текущий период", previousLabel = "Предыдущий период" },
  ref
) {
  if (!data.length) return null;

  const sorted = [...data].sort((a, b) => b.current_count - a.current_count);
  const techNames = sorted.map((d) => d.tech);
  const currentValues = sorted.map((d) => d.current_count);
  const previousValues = sorted.map((d) => d.previous_count ?? 0);

  const option = {
    tooltip: {
      trigger: "axis" as const,
      appendToBody: true,
      confine: false,
      axisPointer: { type: "shadow" as const },
      position: (
        point: number[],
        _params: unknown,
        _dom: HTMLElement,
        _rect: unknown,
        size: { contentSize: number[] }
      ) => {
        const ch = size.contentSize[1] ?? 80;
        return [point[0] + 16, point[1] - ch / 2];
      },
      formatter: (params: { dataIndex: number; seriesIndex: number; color: string }[]) => {
        const item = sorted[params[0].dataIndex];
        let html = `<div style="font-weight:600;margin-bottom:4px">${item.tech}</div>`;
        html += `<div style="color:#6b7280;font-size:12px;margin-bottom:6px">${item.category}</div>`;
        for (const p of params) {
          const isPrev = p.seriesIndex === 1;
          const count = isPrev ? item.previous_count ?? 0 : item.current_count;
          const pct = isPrev ? item.previous_pct ?? 0 : item.current_pct;
          html += `<div style="display:flex;align-items:center;gap:8px;margin:2px 0">
            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${p.color}"></span>
            <span style="color:#374151">${isPrev ? previousLabel : currentLabel}:</span>
            <strong>${count} вак. с упоминанием</strong>
            <span style="color:#9ca3af">(${pct}% от вакансий в периоде)</span>
          </div>`;
        }
        return html;
      },
    },
    grid: { left: 140, right: 44, top: 8, bottom: 8, containLabel: false },
    xAxis: {
      type: "value" as const,
      axisLabel: { formatter: "{value}", color: "#9ca3af", fontSize: 10 },
      splitLine: { lineStyle: { color: "#f3f4f6" } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: "category" as const,
      data: techNames,
      inverse: true,
      axisLabel: { color: "#374151", fontSize: 11, width: 132, overflow: "truncate" as const },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        name: currentLabel,
        type: "bar" as const,
        data: currentValues.map((val, i) => ({
          value: val,
          itemStyle: {
            color: CATEGORY_COLORS[sorted[i].category] ?? "#0abf53",
            borderRadius: [0, 4, 4, 0],
          },
        })),
        barMaxWidth: 20,
        label: { show: true, position: "right" as const, formatter: "{c}", color: "#9ca3af", fontSize: 10 },
        emphasis: { focus: "series" as const },
      },
      ...(compare
        ? [
            {
              name: previousLabel,
              type: "bar" as const,
              data: previousValues.map((val) => ({
                value: val,
                itemStyle: { color: "#bfdbfe", borderRadius: [0, 4, 4, 0] },
              })),
              barMaxWidth: 20,
              label: { show: false },
              emphasis: { focus: "series" as const },
            },
          ]
        : []),
    ],
  };

  return (
    <div className="relative h-full w-full min-h-[200px] overflow-visible">
      <ReactECharts
        ref={ref as never}
        option={option}
        style={{ height: "100%", width: "100%", minHeight: 200 }}
        notMerge={false}
      />
    </div>
  );
});
